import { requestApiSaga } from '@client/common/redux/api/sagas';
import {
  createRemoteRecordCleanUpSaga,
  REMOTE_RECORD_CLEAN_UP_DEBOUNCE,
  REMOTE_RECORD_LIMITS,
} from '@client/common/redux/effects/createRemoteRecordCleanUpSaga';
import { reportError } from '@client/common/redux/errors/actions';
import { parseError } from '@client/common/utils/api/error';
import { ProductTile } from '@client/product/api/ProductTile';
import {
  fetchProductTile,
  fetchProductTileCleanUp,
  fetchProductTileFulfilled,
  fetchProductTileRejected,
  fetchProductTiles,
  fetchProductTilesFulfilled,
  fetchProductTilesRejected,
  selectProductsTilesRecords,
} from '@client/product/redux/products/tile/redux';
import { AxiosResponse } from 'axios';
import {
  all,
  AllEffect,
  call,
  CallEffect,
  debounce,
  delay,
  ForkEffect,
  put,
  PutEffect,
  SelectEffect,
  takeEvery,
} from 'redux-saga/effects';
import { ProfileType } from 'b2b-common/core/context/Context.types';
import { ProductTileResponseType, ProductTilesResponseType } from 'b2b-common/shop/product/api/ProductTile.types';

const FETCH_PRODUCT_TILES_DELAY = 250;

function* fetchProductTileSaga(
  action: ReturnType<typeof fetchProductTile>,
): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  AxiosResponse<ProductTileResponseType> & ProfileType
> {
  const { sku } = action.payload;

  try {
    const response = yield call(requestApiSaga, ProductTile.fetch(sku));
    const productTile = ProductTile.mapResponseToState(response);

    yield put(fetchProductTileFulfilled({ sku, data: productTile }));
  } catch (error) {
    yield put(fetchProductTileRejected({ sku, error: parseError(error) }));
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

function* fetchProductTilesSaga(
  action: ReturnType<typeof fetchProductTiles>,
): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  AxiosResponse<ProductTilesResponseType> & ProfileType
> {
  const { groupNumber, skus } = action.payload;

  if (groupNumber !== 0) {
    yield delay(groupNumber * FETCH_PRODUCT_TILES_DELAY);
  }

  try {
    const response = yield call(
      requestApiSaga,
      ProductTile.fetchMultiple(skus),
    );
    const productTiles = ProductTile.mapMultipleTilesResponseToState(response);
    yield put(fetchProductTilesFulfilled({ skus, data: productTiles }));
  } catch (error) {
    yield put(fetchProductTilesRejected({ skus, error: parseError(error) }));
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

export function* productTilesSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeEvery(fetchProductTile.type, fetchProductTileSaga),
    takeEvery(fetchProductTiles.type, fetchProductTilesSaga),
    debounce(
      REMOTE_RECORD_CLEAN_UP_DEBOUNCE,
      [
        fetchProductTileFulfilled.type,
        fetchProductTileRejected.type,
        fetchProductTilesFulfilled.type,
        fetchProductTilesRejected.type,
      ],
      createRemoteRecordCleanUpSaga(
        selectProductsTilesRecords,
        fetchProductTileCleanUp.type,
        REMOTE_RECORD_LIMITS.xlarge,
      ),
    ),
  ]);
}
